import { useState, type FormEvent, type ReactNode } from "react";
import { errorMessage, isCode } from "../api/client.ts";
import { useT } from "../i18n/index.tsx";
import { CodeInput } from "./CodeInput.tsx";
import { Dialog } from "./Dialog.tsx";
import { Alert, Spinner } from "./ui.tsx";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Extra form fields shown above the code. */
  children?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Ask for the authenticator code (step-up). */
  needsCode?: boolean;
  canSubmit?: boolean;
  /** Throw to show an error; resolve to close. */
  onConfirm: (code: string) => Promise<void>;
};

/** Confirm a sensitive action, usually with the current authenticator code. */
export function StepUpDialog({ open, onClose, title, description, children, confirmLabel, danger, needsCode = true, canSubmit = true, onConfirm }: Props) {
  const t = useT();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setCode("");
    setCodeError(null);
    setError(null);
    onClose();
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (needsCode && code.length !== 6) {
      setCodeError(t.auth.enterAuthenticatorCode);
      return;
    }
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await onConfirm(code);
      setBusy(false);
      setCode("");
      onClose();
    } catch (err) {
      setBusy(false);
      if (isCode(err, "mfa_required")) {
        setCodeError(t.auth.codeRejectedStepUp);
        setCode("");
      } else setError(errorMessage(err));
    }
  };

  return (
    <Dialog open={open} onClose={close} title={title} description={description} size="sm" busy={busy}>
      <form onSubmit={submit} className="form-stack" noValidate>
        {children}
        {needsCode && <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} hint={t.auth.stepUpHint} />}
        {error && <Alert tone="error">{error}</Alert>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>{t.common.cancel}</button>
          <button type="submit" className={`btn ${danger ? "btn-danger" : "btn-primary"}`} disabled={busy || !canSubmit}>
            {busy && <Spinner />} {confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
